import { useRequestOauthToken } from "@tandemdiabetes/sugarmate-api-react-client"
import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import { useNavigate } from "@tanstack/react-router"
import {
  useCallback,
  useMemo,
} from "react"

import { useOauthAuthorize } from "./useOauthAuthorize"

import { useOauthAuthorizeContext } from "src/contexts"
import { useCreatePhoneWithAccessToken } from "src/hooks"
import { RegistrationFlow } from "src/pages/SignUp"
import type { RegistrationForm } from "src/pages/SignUp"
import {
  CreatePhoneErrorToast,
  SignInAfterSignUpErrorToast,
} from "src/toasts"
import {
  injectProps,
  getSugarmateError,
} from "src/utils"

export function OauthAuthorizeSignUp() {
  const requestOauthToken = useRequestOauthToken()
  const oauthAuthorize = useOauthAuthorize()
  const { showToast } = useToasterContext()
  const navigate = useNavigate()
  const createPhone = useCreatePhoneWithAccessToken()

  const {
    p,
    device_type,
    client_id,
    redirect_uri,
  } = useOauthAuthorizeContext()

  const onAccountCreate = useMemo(
    () => (!requestOauthToken || !oauthAuthorize)
      ? undefined
      : async (registrationForm: RegistrationForm) => {
        try {
          const response = await requestOauthToken({
            username: registrationForm.emailAddress,
            password: registrationForm.password,
            client_id,
            grant_type: "password",
            p,
            device_type,
          })

          if (registrationForm.phoneNumber) {
            void createPhone?.(
              {
                phone_number: registrationForm.phoneNumber,
                is_primary: true,
              },
              response.data.access_token,
            ).catch((e) => {
              const sugarmateError = getSugarmateError(e)

              const ErrorToast = injectProps(
                CreatePhoneErrorToast,
                {
                  code: sugarmateError?.code,
                  status: sugarmateError?.status,
                },
              )

              showToast(
                "create-phone-error-toast",
                ErrorToast,
              )
            })
          }

          await oauthAuthorize(response.data.access_token)
        } catch {
          showToast(
            "sign-in-after-sign-up-error-toast",
            SignInAfterSignUpErrorToast,
          )
        }
      },
    [
      p,
      device_type,
      client_id,
      requestOauthToken,
      oauthAuthorize,
      createPhone,
      showToast,
    ],
  )

  const goBack = useCallback(
    () => {
      if (redirect_uri) {
        location.replace(redirect_uri)
      } else {
        void navigate({ to: "/oauth/authorize/" })
      }
    },
    [
      navigate,
      redirect_uri,
    ],
  )

  return (
    <RegistrationFlow
      onAccountCreate={onAccountCreate}
      goBack={goBack}
    />
  )
}
